import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabaseAuth } from '../../services/supabaseAuth';
import { User } from '../../services/userAuth';
import { logger } from '../../utils/logger';
import { getTranslation } from '../../utils/translationHelpers';
import { AuthModal } from './AuthModal';
import './RegistrationModal.css';
import './LoginModal.css';

const GOOGLE_SVG = (
  <>
    <svg className="auth-google-logo" width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  </>
);

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
  nextTestId?: string;
  returnTo?: string;
  /** Full-page /signup: same card shell as login, no AuthModal scrim. */
  fullPage?: boolean;
  /** Full-page: navigate to sign-in (e.g. /login) with return state. */
  onSwitchToSignIn?: () => void;
}

export const RegistrationModal: React.FC<RegistrationModalProps> = ({
  isOpen,
  onClose,
  onSuccess: _onSuccess,
  nextTestId,
  returnTo,
  fullPage = false,
  onSwitchToSignIn,
}) => {
  const { t_nested } = useLanguage();
  const [email, setEmail] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const persistAuthReturnTarget = () => {
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      localStorage.setItem('authReturnTo', returnTo);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError(null);

    try {
      const currentTestId = localStorage.getItem('currentResultsTestId');
      if (currentTestId) {
        localStorage.setItem('pendingReturnTestId', currentTestId);
        localStorage.setItem('returnToResults', 'true');
      }

      if (nextTestId) {
        localStorage.setItem('pendingNextTestId', nextTestId);
      }

      persistAuthReturnTarget();
      await supabaseAuth.signInWithGoogle();
      setIsGoogleLoading(false);
    } catch (error: unknown) {
      logger.error('Google sign-in error:', error);
      if (!window.location.href.includes('accounts.google.com')) {
        setError(
          (error as { message?: string })?.message ||
            getTranslation(t_nested, 'auth.googleSignInFailed', 'Google sign-in failed. Please try again.')
        );
        setIsGoogleLoading(false);
      }
    }
  };

  const handleEmailMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsEmailLoading(true);

    if (!email || !email.includes('@')) {
      setError(getTranslation(t_nested, 'auth.invalidEmail', 'Please enter a valid email address'));
      setIsEmailLoading(false);
      return;
    }

    try {
      const currentTestId = localStorage.getItem('currentResultsTestId');
      if (currentTestId) {
        localStorage.setItem('pendingReturnTestId', currentTestId);
        localStorage.setItem('returnToResults', 'true');
      }

      if (nextTestId) {
        localStorage.setItem('pendingNextTestId', nextTestId);
      }

      persistAuthReturnTarget();
      const result = await supabaseAuth.signInWithMagicLink(email);

      if (result.error) {
        throw result.error;
      }

      setMagicLinkSent(true);
    } catch (error: unknown) {
      logger.error('Magic link error:', error);
      setError(
        (error as { message?: string })?.message ||
          getTranslation(t_nested, 'auth.magicLinkSendFailed', 'Failed to send magic link. Please try again.')
      );
    } finally {
      setIsEmailLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  // ── Full page (/signup): Chicky-style layout, TheoryCoach colors via LoginPage.css + LoginModal.css
  if (fullPage) {
    return (
      <div className="login-modal-content">
        <div className="auth-modal-header">
          <h2 className="auth-modal-title">
            {getTranslation(t_nested,'auth.getStarted', 'Get started')}
          </h2>
          <p className="auth-modal-subtitle">
            {getTranslation(t_nested,'auth.getStartedSubtitle', 'Create your account to track your progress')}
          </p>
        </div>

        {error && <div className="auth-error-message registration-fullpage-error">{error}</div>}

        {magicLinkSent ? (
          <div className="registration-fullpage-magic-sent">
            <p className="auth-magic-link-success">
              {getTranslation(t_nested,
                'onboarding.magicLinkSent',
                'Check your email! We sent you a secure sign-in link.'
              )}
            </p>
            <p className="registration-fullpage-magic-sub">
              {getTranslation(t_nested,
                'onboarding.magicLinkSubtext',
                'Click the link in the email to sign in instantly.'
              )}
            </p>
            <button
              type="button"
              className="auth-modal-button auth-modal-button-primary"
              onClick={() => {
                setMagicLinkSent(false);
                setEmail('');
                setError(null);
              }}
            >
              {getTranslation(t_nested,'onboarding.backToOptions', 'Back')}
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className="auth-modal-button auth-modal-button-google auth-modal-button-google-first"
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading || isEmailLoading}
            >
              {isGoogleLoading ? (
                <div className="auth-loading-spinner" />
              ) : (
                <>
                  {GOOGLE_SVG}
                  <span>
                    {getTranslation(t_nested,'onboarding.continueWithGoogle', 'Continue with Google')}
                  </span>
                </>
              )}
            </button>

            <div className="auth-modal-divider">
              <span className="auth-modal-divider-text">
                {getTranslation(t_nested,'auth.or', 'or')}
              </span>
            </div>

            <form className="auth-modal-form" onSubmit={handleEmailMagicLink} dir="auto">
              <div className="auth-input-group">
                <input
                  type="email"
                  className="auth-modal-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={getTranslation(t_nested,'auth.login.emailPlaceholder', 'Email')}
                  required
                  disabled={isEmailLoading}
                  autoComplete="email"
                />
              </div>
              <button
                type="submit"
                className="auth-modal-button auth-modal-button-magic"
                disabled={isEmailLoading || isGoogleLoading}
              >
                {isEmailLoading ? (
                  <div className="auth-loading-spinner" />
                ) : (
                  getTranslation(t_nested,'onboarding.sendMagicLink', 'Send magic link')
                )}
              </button>
            </form>

            <div className="auth-modal-footer">
              <span className="auth-modal-footer-text">
                {getTranslation(t_nested,'auth.haveAccount', 'Already have an account? ')}
              </span>
              <button
                type="button"
                className="auth-modal-footer-link"
                onClick={() => onSwitchToSignIn?.()}
                disabled={isGoogleLoading || isEmailLoading}
              >
                {getTranslation(t_nested,'auth.signIn', 'Sign In')}
              </button>
            </div>

            <p className="registration-terms-line">
              {getTranslation(t_nested,'onboarding.termsFooter', 'By continuing, you agree to our Terms & Privacy.')}
            </p>
          </>
        )}
      </div>
    );
  }

  // ── Modal (mock results, etc.): soft-gate copy + AuthModal
  return (
    <AuthModal isOpen={isOpen} onClose={onClose}>
      <div className="registration-modal-content">
        <div className="registration-modal-header">
          <div className="registration-modal-checkmark" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <circle cx="14" cy="14" r="14" fill="rgba(34,197,94,0.12)" />
              <circle cx="14" cy="14" r="11" fill="rgba(34,197,94,0.18)" />
              <polyline points="8,14 12,18 20,10" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </div>
          <h2 className="registration-modal-title">
            {getTranslation(t_nested,'gates.softGate.title', 'Save your progress — it\'s free')}
          </h2>
          <p className="registration-modal-subtext">
            {getTranslation(t_nested,'gates.softGate.message', 'Your first AI exam is now available.')}
          </p>
          <p className="registration-modal-nudge">
            {getTranslation(t_nested,'gates.softGate.nudge', 'Great start — pick up where you left off')}
          </p>
          <div className="registration-modal-perks">
            <span className="registration-modal-perk">✓ {getTranslation(t_nested,'gates.softGate.perkScore', 'Track your score')}</span>
            <span className="registration-modal-perk">✓ {getTranslation(t_nested,'gates.softGate.perkAi', 'Personalised exam')}</span>
            <span className="registration-modal-perk">✓ {getTranslation(t_nested,'gates.softGate.perkFree', 'Free')}</span>
          </div>
        </div>

        {error && (
          <div className="registration-error-message">
            {error}
          </div>
        )}

        {magicLinkSent ? (
          <div className="registration-magic-link-sent">
            <p className="magic-link-message">
              {getTranslation(t_nested,'onboarding.magicLinkSent', 'Check your email! We sent you a secure sign-in link.')}
            </p>
            <p className="magic-link-subtext">
              {getTranslation(t_nested,'onboarding.magicLinkSubtext', 'Click the link in the email to sign in instantly.')}
            </p>
            <button
              type="button"
              className="registration-back-button"
              onClick={() => {
                setMagicLinkSent(false);
                setEmail('');
                setError(null);
              }}
            >
              {getTranslation(t_nested,'onboarding.backToOptions', 'Back')}
            </button>
          </div>
        ) : (
          <>
            <div className="registration-modal-actions">
              <button
                className="registration-button registration-button-primary"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading || isEmailLoading}
                type="button"
              >
                {isGoogleLoading ? (
                  <div className="registration-loading-spinner" />
                ) : (
                  <>
                    <svg className="registration-google-icon" width="20" height="20" viewBox="0 0 24 24" aria-hidden>
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {getTranslation(t_nested,'gates.softGate.ctaGoogle', 'Continue practising')}
                  </>
                )}
              </button>

              <button
                className="registration-button registration-button-secondary"
                onClick={() => {
                  const emailForm = document.querySelector('.registration-email-form') as HTMLElement;
                  if (emailForm) {
                    emailForm.style.display = emailForm.style.display === 'none' ? 'flex' : 'none';
                  }
                }}
                disabled={isGoogleLoading || isEmailLoading}
                type="button"
              >
                {getTranslation(t_nested,'onboarding.continueWithEmail', 'Continue with Email')}
              </button>
            </div>

            <form
              className="registration-email-form"
              onSubmit={handleEmailMagicLink}
              style={{ display: 'none' }}
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={getTranslation(t_nested,'auth.emailPlaceholder', 'Enter your email')}
                className="registration-email-input"
                required
                disabled={isEmailLoading}
              />
              <button
                type="submit"
                className="registration-button registration-button-primary"
                disabled={isEmailLoading}
              >
                {isEmailLoading ? (
                  <div className="registration-loading-spinner" />
                ) : (
                  getTranslation(t_nested,'onboarding.sendMagicLink', 'Send magic link')
                )}
              </button>
            </form>

            <p className="registration-footer">
              {getTranslation(t_nested,'onboarding.termsFooter', 'By continuing, you agree to our Terms & Privacy.')}
            </p>
          </>
        )}
      </div>
    </AuthModal>
  );
};
